import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../app/appHooks";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { getUnitTitle } from "../dashboard/CommunityUnit";
import {
  CommunityTypes,
  getRecommendationsHtml,
  Views,
} from "../../helpers/constants";
import { useHistory } from "react-router-dom";
import { Grid, Button } from "@material-ui/core";
import Icons from "../../helpers/iconImports";
import { IReport } from "../../services/dashboard.services";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionTitle: {
      marginBottom: 0,
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(2),
      textAlign: "center",
      fontSize: theme.typography.body1.fontSize,
      fontWeight: "bold",
    },
    backButtonContainer: {
      width: "100%",
      textAlign: "left",
      paddingTop: "10px",
      paddingLeft: theme.spacing(1.5),
    },
    backButton: {
      color: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
    },
    printReportContainer: {
      width: "100%",
      textAlign: "right",
      paddingTop: "6px",
      paddingRight: theme.spacing(2),
    },
    printReportButton: {
      color: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: "transparent",
        textDecoration: "underline",
      },
    },
    buttons: {
      fontSize: theme.typography.caption.fontSize,
      lineHeight: 1,
    },
    icon: {
      width: "80%",
    },
    [`@media print`]: {
      sectionTitle: {
        display: "none",
      },
      printReportButton: {
        display: "none",
      },
      backButton: {
        display: "none",
      },
    },
    reportHtmlBox: {
      width: "1100px",
      height: "100%",
      background: "#fff",
      border: "1px solid #ccc",
      borderRadius: 4,
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      marginLeft: "auto",
      marginRight: "auto",
      display: "block",
    },
  })
);

export function ReportsPrintableViewer(props: IProps) {
  const { report, recommendationText, communityName } = props;

  /* Hooks */
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  /* Selectors */
  const selectedUnit = useAppSelector((state) =>
    state.reportsState.units?.find(
      (unit) => unit.id === state.reportsState.selectedUnit
    )
  );

  const isAipCommunity = useAppSelector(
    (state) =>
      state.headerState.selectedCommunity?.community_type === CommunityTypes.aip
  );

  let listItemText = undefined;
  if (isAipCommunity) {
    listItemText = { unitTitle: t("reports"), unitSubtitle: "" };
  } else if (selectedUnit) {
    listItemText = getUnitTitle(selectedUnit);
  }

  const reportsRedirect = () => {
    history.push(Views.REPORTS);
  };

  useEffect(() => {
    if (selectedUnit === undefined) {
      reportsRedirect();
    }
  });

  let reportHtml = report.report;
  if (!isAipCommunity) {
    const recommendationsHtml = getRecommendationsHtml({
      recommendationText,
      communityName,
    });

    if (reportHtml.includes('<div id="recommendations-placeholder"></div>')) {
      reportHtml = reportHtml.replace(
        '<div id="recommendations-placeholder"></div>',
        recommendationsHtml
      );
    }
  }

  // Extra logic required due to how Safari handles onLoad
  const printReport = () => {
    const printWindow = window.open("", "_blank", "width=1100,height=700");
    if (printWindow) {
      // document.write is deprecated but safe for this use case
      printWindow.document.write(`
        <html>
          <head>
            <title>${t("activity_report")}</title>
            <style>
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>
            ${reportHtml}
            <script>
              window.onload = function() {
                const images = Array.from(document.images);
                if (images.length === 0) {
                  window.print();
                  window.close();
                  return;
                }
                let loaded = 0;
                const checkDone = () => {
                  loaded++;
                  if (loaded === images.length) {
                    window.print();
                    window.close();
                  }
                };
                images.forEach(img => {
                  if (img.complete) {
                    checkDone();
                  } else {
                    img.addEventListener('load', checkDone, { once: true });
                    img.addEventListener('error', checkDone, { once: true });
                  }
                });
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <React.Fragment>
      <React.Fragment>
        <Grid container>
          <Grid item xs={2} className={classes.backButtonContainer}>
            {selectedUnit && (
              <Button
                size="small"
                className={classes.backButton}
                classes={{ label: classes.buttons }}
                onClick={reportsRedirect}
              >
                {t("action_back")}
              </Button>
            )}
          </Grid>
          <Grid item xs={8}>
            {listItemText && (
              <div className={classes.sectionTitle}>
                {`${listItemText.unitTitle}${
                  listItemText.unitSubtitle !== ""
                    ? ` - ${listItemText.unitSubtitle}`
                    : ""
                }`}
              </div>
            )}
          </Grid>
          <Grid item xs={2} className={classes.printReportContainer}>
            {selectedUnit && (
              <Button
                size="small"
                className={classes.printReportButton}
                classes={{ label: classes.buttons }}
                endIcon={
                  <img
                    src={Icons.PrintReportIcon}
                    alt={""}
                    draggable={false}
                    className={classes.icon}
                  />
                }
                onClick={printReport}
              >
                {t("print_report")}
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <iframe
            title="Report Preview"
            srcDoc={reportHtml}
            className={classes.reportHtmlBox}
          />
        </Grid>
      </React.Fragment>
    </React.Fragment>
  );
}

interface IProps {
  report: IReport;
  recommendationText?: string;
  communityName?: string;
}
